import { Colors, DivergingBarChart, Text } from '@amelio/ui-library';

import { ChartLegendFooter } from './ChartLegendFooter';
import { NotRepresentativePill } from './NotRepresentativePill';
import { CARD_SECTION, CHART_INTRO } from '../calibrationStyles';
import { buildDeviationAxis } from '../utils/deviationAxis';
import { formatSignedPoints } from '../utils/formatSignedPoints';

import type { CalibrationReport } from '@/types/calibration';
import type { DivergingBarRow } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getFullName } from '@/utils/user';

export interface ManagerDeviationViewProps {
    report: CalibrationReport;
    language: string;
}

/** "Rating level" pivot: one bar per manager, showing how their average deviates from the
 * cohort, framed by what the chart is for and closed by the cohort's own figures. */
export const ManagerDeviationView = ({ report, language }: ManagerDeviationViewProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    const rows: DivergingBarRow[] = report.managers.map((manager) => ({
        id: manager.managerId,
        label: getFullName(manager),
        secondaryLabel: t('calibration.deviation.evaluatedCount', { count: manager.headcount }),
        badge: manager.isRepresentative ? undefined : (
            <NotRepresentativePill
                minimumHeadcount={report.minimumRepresentativeHeadcount}
                dataTestId={`calibration-not-representative-${manager.managerId}`}
            />
        ),
        value: manager.deviation,
        formattedValue: formatSignedPoints({ value: manager.deviation, language }),
        subRows: manager.employees.map((employee) => ({
            id: employee.employeeId,
            label: getFullName(employee),
            value: employee.deviation,
            formattedValue: formatSignedPoints({ value: employee.deviation, language }),
        })),
    }));

    const axis = buildDeviationAxis({
        axisPoints: report.deviationAxisPoints,
        language,
        centerLabel: t('calibration.deviation.cohort'),
    });

    return (
        <div className={CARD_SECTION}>
            <Text
                color={Colors.BASE_500}
                className={CHART_INTRO}
            >
                {t('calibration.deviation.intro')}
            </Text>

            <DivergingBarChart
                rows={rows}
                axis={axis}
                negativeLabel={t('calibration.deviation.moreSevere')}
                positiveLabel={t('calibration.deviation.moreLenient')}
                clippedLabel={t('calibration.deviation.offAxis')}
                legend={
                    <ChartLegendFooter
                        report={report}
                        language={language}
                    />
                }
                ariaLabel={t('calibration.deviation.ariaLabel', {
                    managerLabel: getManagerLabel(),
                })}
                chevronPlacement={'start'}
                dataTestId={'calibration-deviation-chart'}
            />
        </div>
    );
};
